import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import PhotoUpload from "./PhotoUpload";
import { DEPARTMENTS, REGIONS } from "../data/departments";

const AVATARS = ["🔮","🌙","🦋","🌹","🐍","🍷","🎭","✨","💜","🫦","🔥","🪷","🌊","🐺","💫","🖤"];

const GENDER_OPTIONS = [
  { value: "", label: "Non précisé" },
  { value: "homme", label: "Homme" },
  { value: "femme", label: "Femme" },
  { value: "non-binaire", label: "Non-binaire" },
  { value: "autre", label: "Autre" },
];

const ORIENTATION_OPTIONS = [
  { value: "", label: "Non précisé" },
  { value: "hétéro", label: "Hétéro" },
  { value: "bi", label: "Bi" },
  { value: "gay", label: "Gay" },
  { value: "lesbienne", label: "Lesbienne" },
  { value: "pan", label: "Pan" },
  { value: "curieux·se", label: "Curieux·se" },
  { value: "autre", label: "Autre" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Non précisé" },
  { value: "solo", label: "Solo" },
  { value: "en couple", label: "En couple" },
  { value: "couple ouvert", label: "Couple ouvert" },
  { value: "polyamoureux·se", label: "Polyamoureux·se" },
  { value: "autre", label: "Autre" },
];

const LEVEL_OPTIONS = [
  { value: "", label: "Non précisé" },
  { value: "curieux·se", label: "Curieux·se" },
  { value: "débutant·e", label: "Débutant·e" },
  { value: "expérimenté·e", label: "Expérimenté·e" },
  { value: "confirmé·e", label: "Confirmé·e" },
];

const LOOKING_FOR_OPTIONS = [
  "Discussions", "Amitié libertine", "Rencontres soft", "Soirées privées",
  "Plan couple", "Plan solo", "Échangisme", "Mélangisme",
  "Voyeurisme/Exhib", "BDSM", "Polyamour", "Sorties clubs",
];

const INTEREST_SUGGESTIONS = [
  "Soirées", "Clubs", "Voyages", "Saunas", "Tantra", "Shibari",
  "Photographie", "Danse", "Bien-être", "Massage", "Littérature érotique",
  "Yoga", "Gastronomie", "Vin", "Cinéma", "Musique",
];

// ── Badge certifié ────────────────────────────────────────────
function CertifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-sensoria-accent px-3 py-1 text-xs font-bold text-white shadow">
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
      </svg>
      Certifié
    </span>
  );
}

export default function ProfileEdit() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [profile, setProfile] = useState({
    display_name: "", bio: "", avatar_emoji: "🔮", city: "", age: "",
    gender: "", orientation: "", relationship_status: "",
    looking_for: [], interests: [], boundaries: "",
    experience_level: "", is_visible: true, show_age: true, allow_messages: true,
    department: "", region: "", is_ghost_mode: false,
  });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [message,     setMessage]     = useState({ type: "", text: "" });
  const [newInterest, setNewInterest] = useState("");

  // Certification
  const [verif,        setVerif]        = useState(null); // { request, is_verified, verified_at }
  const [verifMsg,     setVerifMsg]     = useState("");
  const [verifPhoto,   setVerifPhoto]   = useState(null);   // File objet
  const [verifPhotoPreview, setVerifPhotoPreview] = useState(null); // data URL
  const [verifLoading, setVerifLoading] = useState(false);
  const [verifResult,  setVerifResult]  = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const data = await api("/api/profiles/me");
        if (data.profile) setProfile((prev) => ({ ...prev, ...data.profile, age: data.profile.age || "" }));
      } catch (err) { console.error(err); }
      try {
        const v = await api("/api/profiles/verification-status");
        setVerif(v);
      } catch {}
      setLoading(false);
    }
    load();
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await api("/api/profiles", { method: "PUT", body: JSON.stringify(profile) });
      setMessage({ type: "success", text: t("profile_saved") });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally { setSaving(false); }
  }

  function handleVerifPhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setVerifPhoto(file);
    const reader = new FileReader();
    reader.onload = ev => setVerifPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  async function requestVerification(e) {
    e.preventDefault();
    if (!verifPhoto) {
      setVerifResult({ ok: false, text: "La photo de vérification est obligatoire." });
      return;
    }
    setVerifLoading(true);
    setVerifResult(null);
    try {
      // 1. Uploader la photo via l'API photos (entity_type=verification)
      const formData = new FormData();
      formData.append("photo", verifPhoto);
      formData.append("entity_type", "verification");
      formData.append("entity_id", "0");
      const token = localStorage.getItem("sensoria_token");
      const uploadRes = await fetch("/api/photos/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || "Erreur upload photo");
      }
      const uploadData = await uploadRes.json();
      const photoUrl = uploadData.url || uploadData.photo?.url;

      // 2. Soumettre la demande avec l'URL de la photo
      await api("/api/profiles/request-verification", {
        method: "POST",
        body: JSON.stringify({ message: verifMsg, photo_verification_url: photoUrl }),
      });
      setVerifResult({ ok: true, text: "Demande envoyée ! L'équipe examine ton profil sous 48h." });
      setVerif(v => ({ ...(v || {}), request: { status: "pending" } }));
    } catch (err) {
      setVerifResult({ ok: false, text: err.message });
    } finally {
      setVerifLoading(false);
    }
  }

  function toggleArrayItem(field, item) {
    setProfile((p) => ({
      ...p,
      [field]: p[field].includes(item) ? p[field].filter((i) => i !== item) : [...p[field], item],
    }));
  }

  function addInterest(interest) {
    const trimmed = interest.trim();
    if (trimmed && profile.interests.length < 15 && !profile.interests.includes(trimmed)) {
      setProfile((p) => ({ ...p, interests: [...p.interests, trimmed] }));
    }
    setNewInterest("");
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
      </div>
    );
  }

  // ── Statut certification ──
  const isCertified    = verif?.is_verified === 1;
  const requestPending = verif?.request?.status === "pending";
  const requestRejected= verif?.request?.status === "rejected";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">{t("profile_title")}</h2>
          <p className="mt-1 text-sm text-sensoria-text-dim">{t("profile_desc")}</p>
        </div>
        {isCertified && <CertifiedBadge />}
      </div>

      {/* ── Bloc certification ── */}
      <div className={`sensoria-card border ${
        isCertified    ? "border-sensoria-accent/40 bg-sensoria-accent/5" :
        requestPending ? "border-amber-500/30 bg-amber-500/5" :
                        "border-sensoria-border/30"
      }`}>
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg ${
            isCertified ? "bg-sensoria-accent text-white" :
            requestPending ? "bg-amber-500/20 text-amber-400" :
            "bg-sensoria-surface text-sensoria-muted"
          }`}>
            {isCertified ? (
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            ) : requestPending ? "⏳" : "🏅"}
          </div>
          <div className="flex-1 min-w-0">
            {isCertified ? (
              <>
                <p className="font-semibold text-white">Profil certifié ✓</p>
                <p className="mt-0.5 text-xs text-sensoria-text-dim">
                  Ton profil porte le badge <strong className="text-sensoria-accent-light">Certifié</strong> dans l'annuaire et sur ta page publique.
                  {verif?.verified_at && <span className="ml-1 text-sensoria-muted">Depuis le {new Date(verif.verified_at).toLocaleDateString("fr-FR")}.</span>}
                </p>
              </>
            ) : requestPending ? (
              <>
                <p className="font-semibold text-amber-300">Demande en attente</p>
                <p className="mt-0.5 text-xs text-sensoria-text-dim">
                  Ta demande de certification est en cours d'examen. L'équipe te répondra sous 48h.
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold text-white">Demander la certification</p>
                <p className="mt-0.5 text-xs text-sensoria-text-dim">
                  Le badge <strong className="text-sensoria-accent-light">Certifié</strong> est attribué par l'équipe après vérification de ton profil.
                  {requestRejected && <span className="ml-1 text-red-400">Ta demande précédente a été refusée. Tu peux re-soumettre.</span>}
                </p>
                <form onSubmit={requestVerification} className="mt-3 space-y-3">
                  {/* Photo de vérification */}
                  <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sensoria-text-dim">
                      Photo de vérification <span className="text-red-400">*</span>
                    </p>
                    <p className="mb-2 text-[11px] leading-relaxed text-sensoria-muted">
                      Prends un selfie en tenant un papier avec écrit à la main&nbsp;:
                      ton <strong className="text-sensoria-text">pseudo</strong>,
                      la <strong className="text-sensoria-text">date du jour</strong> et le mot
                      <strong className="text-sensoria-text"> "Univers Libertin"</strong>.
                      Photo lisible, visage visible.
                    </p>
                    <label className={`flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed transition-all ${
                      verifPhotoPreview
                        ? "border-sensoria-accent/50 p-1"
                        : "border-sensoria-border/40 hover:border-sensoria-accent/40 p-6"
                    }`}>
                      <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={handleVerifPhotoChange}
                        className="sr-only"
                      />
                      {verifPhotoPreview ? (
                        <div className="relative w-full">
                          <img
                            src={verifPhotoPreview}
                            alt="Aperçu vérification"
                            className="mx-auto max-h-40 rounded-lg object-contain"
                          />
                          <p className="mt-1.5 text-center text-[10px] text-sensoria-accent">
                            Cliquer pour changer la photo
                          </p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="mb-1 text-2xl">📸</div>
                          <p className="text-xs font-medium text-sensoria-text-dim">Cliquer pour choisir une photo</p>
                          <p className="mt-0.5 text-[10px] text-sensoria-muted">JPG, PNG — max 5 Mo</p>
                        </div>
                      )}
                    </label>
                  </div>
                  <textarea
                    value={verifMsg}
                    onChange={e => setVerifMsg(e.target.value)}
                    placeholder="Message optionnel pour l'équipe (présente-toi, explique ta démarche…)"
                    rows={2}
                    maxLength={300}
                    className="sensoria-input resize-none text-sm w-full"
                  />
                  <button
                    type="submit"
                    disabled={verifLoading || !verifPhoto}
                    className="rounded-lg bg-sensoria-accent/15 border border-sensoria-accent/30 px-4 py-2 text-xs font-semibold text-sensoria-accent-light hover:bg-sensoria-accent/25 transition-all disabled:opacity-50"
                  >
                    {verifLoading ? "Envoi…" : "Envoyer la demande →"}
                  </button>
                </form>
                {verifResult && (
                  <p className={`mt-2 text-xs font-medium ${verifResult.ok ? "text-green-400" : "text-red-400"}`}>
                    {verifResult.text}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Avatar */}
        <div className="sensoria-card">
          <label className="mb-3 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Avatar</label>
          <div className="flex flex-wrap gap-2">
            {AVATARS.map((e) => (
              <button key={e} type="button" onClick={() => setProfile((p) => ({ ...p, avatar_emoji: e }))}
                className={`flex h-12 w-12 items-center justify-center rounded-xl text-2xl transition-all ${
                  profile.avatar_emoji === e ? "bg-sensoria-accent/20 ring-2 ring-sensoria-accent" : "bg-sensoria-surface hover:bg-sensoria-border/50"
                }`}>{e}</button>
            ))}
          </div>
        </div>

        {/* Photos */}
        <div className="sensoria-card">
          <label className="mb-3 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">
            Photos de profil
          </label>
          <PhotoUpload entityType="profile" entityId={user?.id || ""} maxPhotos={5} />
          <p className="mt-2 text-[10px] text-sensoria-muted">
            Vous pouvez flouter certaines photos (visibles uniquement pour les Premium).
            Passez Premium pour uploader jusqu'à 10 photos.
          </p>
        </div>

        {/* Identity */}
        <div className="sensoria-card space-y-4">
          <h3 className="text-sm font-semibold text-white">Identité</h3>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Nom affiché</label>
            <input type="text" value={profile.display_name}
              onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))}
              placeholder="Pseudo ou surnom" maxLength={60} className="sensoria-input" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Bio</label>
            <textarea value={profile.bio}
              onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
              placeholder="Décrivez-vous, vos envies, ce qui vous attire..."
              rows={4} maxLength={800} className="sensoria-input resize-none" />
            <span className="mt-1 block text-right text-xs text-sensoria-muted">{profile.bio.length}/800</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Ville</label>
              <input type="text" value={profile.city}
                onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))}
                placeholder="Votre ville" maxLength={80} className="sensoria-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Âge</label>
              <input type="number" value={profile.age}
                onChange={(e) => setProfile((p) => ({ ...p, age: e.target.value }))}
                min={18} max={99} placeholder="18+" className="sensoria-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Expérience</label>
              <select value={profile.experience_level}
                onChange={(e) => setProfile((p) => ({ ...p, experience_level: e.target.value }))}
                className="sensoria-input appearance-none">
                {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Département</label>
              <select value={profile.department}
                onChange={(e) => {
                  const dept = DEPARTMENTS.find((d) => d.code === e.target.value);
                  setProfile((p) => ({ ...p, department: e.target.value, region: dept?.region || p.region }));
                }}
                className="sensoria-input appearance-none">
                <option value="">Non précisé</option>
                {DEPARTMENTS.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Région</label>
              <select value={profile.region}
                onChange={(e) => setProfile((p) => ({ ...p, region: e.target.value }))}
                className="sensoria-input appearance-none">
                <option value="">Non précisé</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Genre</label>
              <select value={profile.gender}
                onChange={(e) => setProfile((p) => ({ ...p, gender: e.target.value }))}
                className="sensoria-input appearance-none">
                {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Orientation</label>
              <select value={profile.orientation}
                onChange={(e) => setProfile((p) => ({ ...p, orientation: e.target.value }))}
                className="sensoria-input appearance-none">
                {ORIENTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Situation</label>
              <select value={profile.relationship_status}
                onChange={(e) => setProfile((p) => ({ ...p, relationship_status: e.target.value }))}
                className="sensoria-input appearance-none">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Looking for */}
        <div className="sensoria-card">
          <label className="mb-3 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">
            Je recherche ({profile.looking_for.length}/8)
          </label>
          <div className="flex flex-wrap gap-2">
            {LOOKING_FOR_OPTIONS.map((opt) => (
              <button key={opt} type="button" onClick={() => toggleArrayItem("looking_for", opt)}
                disabled={!profile.looking_for.includes(opt) && profile.looking_for.length >= 8}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                  profile.looking_for.includes(opt)
                    ? "border-rose-500/50 bg-rose-500/10 text-rose-300"
                    : "border-sensoria-border text-sensoria-text-dim hover:border-sensoria-muted disabled:opacity-30"
                }`}>{opt}</button>
            ))}
          </div>
        </div>

        {/* Interests */}
        <div className="sensoria-card">
          <label className="mb-3 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">
            Centres d'intérêt ({profile.interests.length}/15)
          </label>
          {profile.interests.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {profile.interests.map((int) => (
                <span key={int} className="flex items-center gap-1 rounded-full bg-sensoria-accent/15 px-3 py-1.5 text-xs font-medium text-sensoria-accent-light">
                  {int}
                  <button type="button" onClick={() => toggleArrayItem("interests", int)} className="ml-1 hover:text-white">×</button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="text" value={newInterest}
              onChange={(e) => setNewInterest(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(newInterest); } }}
              placeholder="Ajouter..." maxLength={30} className="sensoria-input flex-1" />
            <button type="button" onClick={() => addInterest(newInterest)}
              disabled={!newInterest.trim() || profile.interests.length >= 15}
              className="sensoria-btn px-4 py-2 text-sm">+</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {INTEREST_SUGGESTIONS.filter((s) => !profile.interests.includes(s)).slice(0, 10).map((s) => (
              <button key={s} type="button" onClick={() => addInterest(s)}
                className="rounded-full border border-sensoria-border px-2.5 py-1 text-[10px] text-sensoria-text-dim hover:border-sensoria-accent/50 hover:text-sensoria-accent-light transition-colors">
                + {s}
              </button>
            ))}
          </div>
        </div>

        {/* Boundaries */}
        <div className="sensoria-card">
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">
            Mes limites & safe words
          </label>
          <textarea value={profile.boundaries}
            onChange={(e) => setProfile((p) => ({ ...p, boundaries: e.target.value }))}
            placeholder="Ce que vous ne souhaitez pas, vos limites claires, votre safe word... (visible uniquement sur votre profil)"
            rows={3} maxLength={500} className="sensoria-input resize-none" />
          <p className="mt-1 text-[10px] text-sensoria-muted">Vos limites sont visibles par les autres membres. Elles seront respectées.</p>
        </div>

        {/* Privacy toggles */}
        <div className="sensoria-card space-y-4">
          <h3 className="text-sm font-semibold text-white">Confidentialité</h3>
          {[
            { key: "is_visible", label: "Visible dans l'annuaire", desc: "Désactivez pour masquer votre profil." },
            { key: "show_age", label: "Afficher mon âge", desc: "Votre âge sera visible sur votre profil." },
            { key: "allow_messages", label: "Accepter les messages privés", desc: "Autorisez les autres membres à vous écrire." },
            { key: "is_ghost_mode", label: "🔮 Mode Fantôme (VIP)", desc: "Visitez les profils sans apparaître dans leur liste de visiteurs." },
          ].map(({ key, label, desc }) => (
            <label key={key} className="flex cursor-pointer items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-white">{label}</span>
                <p className="text-xs text-sensoria-text-dim">{desc}</p>
              </div>
              <div
                onClick={() => setProfile((p) => ({ ...p, [key]: !p[key] }))}
                className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors cursor-pointer ${
                  profile[key] ? "bg-sensoria-accent" : "bg-sensoria-border"
                }`}
              >
                <div className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  profile[key] ? "translate-x-[22px]" : "translate-x-0.5"
                }`} />
              </div>
            </label>
          ))}
        </div>

        {message.text && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${
            message.type === "success"
              ? "border-sensoria-success/30 bg-sensoria-success/10 text-sensoria-success"
              : "border-sensoria-danger/30 bg-sensoria-danger/10 text-sensoria-danger"
          }`}>{message.text}</div>
        )}

        <button type="submit" disabled={saving} className="sensoria-btn w-full">
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              {t("profile_saving")}
            </span>
          ) : t("profile_save")}
        </button>
      </form>
    </div>
  );
}
